import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaEdit, FaTrash, FaImage, FaLink, FaSearch, FaBoxes, FaSync, FaFilter, FaPercent, FaCheckSquare, FaSquare, FaBolt } from 'react-icons/fa';
import { formatMoneyCHF } from '../utils/priceUtils';
import {
  updateProductDiscount,
  removeProductDiscount,
  fetchAllDiscounts,
  fetchDiscountProducts,
  fetchCampaigns,
  updateCampaign,
  fetchCampaignProducts,
  removeProductFromCampaign,
} from '../services/apiService';
import CampaignFormModal from './CampaignFormModal';
import CampaignBulkAddModal from './CampaignBulkAddModal';
import { PageSpinner, CardSkeleton, ButtonSpinner } from './LoadingSpinner';
import Pagination from './Pagination';
import ProductAutocompleteInput from './ProductAutocompleteInput';
import ProductSearchModal from './ProductSearchModal';
import BulkDiscountModal from './BulkDiscountModal';
import DiscountImportModal from './DiscountImportModal';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { getArticleImageUrl } from '../utils/imageUtils';
import PriceDisplay from './PriceDisplay';

// Image dimensions configuration
const IMAGE_DIMENSIONS = {
  thumbnail: { width: 300, height: 300 },
  medium: { width: 600, height: 600 },
  large: { width: 900, height: 900 }
};

// Optimized image component with loading states
const OptimizedImage = ({ src, alt, className, isPriority = false }) => {
  const { t } = useLanguage();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(false);

  return (
    <div className="relative w-full h-full">
      {isLoading && (
        <div className="absolute inset-0 bg-gray-200 animate-pulse" />
      )}
      <picture>
        <img
          src={src}
          alt={alt}
          width={IMAGE_DIMENSIONS.medium.width}
          height={IMAGE_DIMENSIONS.medium.height}
          className={`${className} ${isLoading ? 'opacity-0' : 'opacity-100'} transition-opacity duration-300`}
          loading={isPriority ? "eager" : "lazy"}
          decoding="async"
          onLoad={() => setIsLoading(false)}
          onError={() => {
            setIsLoading(false);
            setError(true);
          }}
        />
      </picture>
      {error && (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-100">
          <span className="text-gray-500">{t('discounts.discountCard.noImage')}</span>
        </div>
      )}
    </div>
  );
};

// Discount Product Card Component - Updated for new API structure
const DiscountProductCard = ({ 
  product, 
  onDelete,
  onSelect,
  isSelected = false,
  isSelectionMode = false
}) => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  // Process product data to match display structure
  const article = product.articles?.[0];
  const discount = article?.discount;

  const processedArticle = {
    pharmacode: article?.pharmacode ?? product.pharmacode,
    description: {
      description: article?.description?.description || 'No description available',
      longDescription: article?.description?.longDescription || 'No long description available'
    },
    hasPhoto: article?.hasPhoto || false,
    images: article?.images || {},
    price: article?.price || null
  };

  // Status badge for live / scheduled / expired discounts. The backend sets
  // discount.status per row (see getAllDiscountProduct). Older API responses
  // may not include it — derive from dates as a safe fallback.
  const statusFromDates = () => {
    if (!discount) return null;
    const now = new Date();
    if (discount.endsAt && new Date(discount.endsAt) <= now) return 'expired';
    if (discount.startsAt && new Date(discount.startsAt) > now) return 'scheduled';
    return 'live';
  };
  const status = discount?.status || statusFromDates();
  const statusStyle = {
    live:      { bg: 'bg-green-100',  text: 'text-green-700',  dot: 'bg-green-500',  label: 'Live' },
    scheduled: { bg: 'bg-amber-100',  text: 'text-amber-700',  dot: 'bg-amber-500',  label: 'Scheduled' },
    expired:   { bg: 'bg-gray-100',   text: 'text-gray-600',   dot: 'bg-gray-400',   label: 'Expired' },
  };
  const badge = status ? statusStyle[status] : null;

  return (
    <motion.div
      layout
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className={`group relative border border-gray-200 rounded-xl bg-white shadow-sm hover:shadow-md transition-shadow duration-200 ${
        isSelected ? 'ring-2 ring-indigo-500 bg-indigo-50/30' : ''
      } ${isSelectionMode ? 'cursor-pointer hover:ring-2 hover:ring-indigo-300' : ''}`}
      onClick={(e) => {
        if (isSelectionMode) {
          // In selection mode, clicking anywhere on the card selects it
          e.preventDefault();
          e.stopPropagation();
          onSelect && onSelect(processedArticle);
        } else if (!e.target.closest('button')) {
          // In normal mode, navigate to product details
          navigate(`/products/${processedArticle.pharmacode}`);
        }
      }}
      style={{ cursor: isSelectionMode ? 'pointer' : 'pointer' }}
    >
      {/* Selection Checkbox - Only show in selection mode as visual indicator */}
      {isSelectionMode && (
        <div className="absolute top-3 left-3 z-10">
          <div
            className={`w-5 h-5 rounded border-2 transition-all duration-200 flex items-center justify-center ${
              isSelected
                ? 'bg-indigo-500 border-indigo-500 text-white shadow-sm'
                : 'bg-white/95 border-gray-300'
            }`}
          >
            {isSelected && <FaCheckSquare className="w-3 h-3" />}
          </div>
        </div>
      )}

      {/* Status badge — live / scheduled / expired */}
      {badge && (
        <div className="absolute top-3 right-3 z-10">
          <span
            className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-[10px] font-semibold uppercase tracking-wide ${badge.bg} ${badge.text} shadow-sm`}
            title={
              discount?.startsAt || discount?.endsAt
                ? `${discount?.startsAt ? 'From ' + new Date(discount.startsAt).toLocaleString() : ''}${discount?.startsAt && discount?.endsAt ? ' · ' : ''}${discount?.endsAt ? 'Until ' + new Date(discount.endsAt).toLocaleString() : ''}`
                : 'Active immediately, no expiry'
            }
          >
            <span className={`w-1.5 h-1.5 rounded-full ${badge.dot} ${status === 'live' ? 'animate-pulse' : ''}`}></span>
            {badge.label}
          </span>
        </div>
      )}
      {/* Fixed height container */}
      <div className="aspect-[4/3] w-full relative">
        {/* Image wrapper */}
        <div className="absolute inset-0 p-3">
          {(() => {
            const imageUrl = getArticleImageUrl(processedArticle, 'F');
            if (!imageUrl) {
              return (
                <div className="w-full h-full flex items-center justify-center text-gray-500 text-sm">
                  <span>{t('discounts.discountCard.noImage')}</span>
                </div>
              );
            }

            return (
              <OptimizedImage
                src={imageUrl}
                alt={processedArticle.description?.longDescription || 'Product Image'}
                className="w-full h-full object-contain"
                isPriority={false}
              />
            );
          })()}
        </div>
      </div>

      {/* Content section */}
      <div className="p-3 flex flex-col min-h-[120px]">
        {/* Title and description */}
        <div className="flex-1">
          <h3 className="text-sm font-medium text-gray-500 line-clamp-2">
            {t('discounts.discountCard.pharmacode')}: {processedArticle.pharmacode}
          </h3>
          <p className="mt-1 text-sm italic text-gray-900 font-medium line-clamp-2">
            {processedArticle.description?.description || t('discounts.discountCard.noDescription')}
          </p>
          <p className="text-sm text-gray-900 font-medium mt-1 line-clamp-2">
            {processedArticle.description?.longDescription || t('discounts.discountCard.noLongDescription')}
          </p>
        </div>
        
        {/* Price with discount information */}
        <div className="mt-2 pt-2 border-t border-gray-100">
          <PriceDisplay article={processedArticle} fallbackKey="discounts.discountCard.noPrice" />
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-2 pt-2 border-t border-gray-100 mt-2">
          <button
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onDelete(processedArticle);
            }}
            className="text-red-500 hover:text-red-700 flex items-center gap-1 text-sm"
          >
            <FaTrash /> {t('discounts.discountCard.removeDiscount')}
          </button>
        </div>
      </div>
    </motion.div>
  );
};

// ── filter helpers (operate on a discount "product" row) ────────────────────
// All fields below are reliably present on every row the backend returns
// (see getAllDiscountProduct / unified discount branch), so client-side
// filtering is safe regardless of which endpoint served the page.
const discountArticle = (product) => product?.articles?.[0] || null;

const discountStatusOf = (product) => {
  const d = discountArticle(product)?.discount;
  if (!d) return null;
  if (d.status) return d.status;
  const now = new Date();
  if (d.endsAt && new Date(d.endsAt) <= now) return 'expired';
  if (d.startsAt && new Date(d.startsAt) > now) return 'scheduled';
  return 'live';
};

const discountKindOf = (product) => discountArticle(product)?.discount?.kind || null;

const discountPriceOf = (product) => {
  const p = discountArticle(product)?.price;
  const v = p?.currentPrice ?? p?.basePrice ?? null;
  return typeof v === 'number' ? v : (v != null ? Number(v) : null);
};

const DiscountManager = () => {
  const { t } = useLanguage();
  const [discountProducts, setDiscountProducts] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [newProduct, setNewProduct] = useState({
    pharmacode: '',
    kind: 'percent', // 'percent' | 'fixed'
    amount: '',
    startsAt: '',    // ISO datetime-local string, optional
    endsAt: '',      // ISO datetime-local string, optional
    label: '',
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [isAddingDiscount, setIsAddingDiscount] = useState(false);
  const [error, setError] = useState(null);
  const [totalDiscounts, setTotalDiscounts] = useState(0);
  const [showProductSearchModal, setShowProductSearchModal] = useState(false);
  const [productSearchTerm, setProductSearchTerm] = useState('');
  
  // Selection state
  const [isSelectionMode, setIsSelectionMode] = useState(false);
  const [selectedDiscounts, setSelectedDiscounts] = useState([]);
  const [isBulkDeleteModalOpen, setIsBulkDeleteModalOpen] = useState(false);
  const [deleteReason, setDeleteReason] = useState('');
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [useUnifiedEndpoint, setUseUnifiedEndpoint] = useState(false);

  // ── Filters (client-side, applied across the full curated discount set) ──
  // The discount list is a curated, bounded collection, so we load it all
  // once and filter/paginate in memory. This makes the filters genuinely
  // functional across every row — not just the visible page — without any
  // backend changes (the unified endpoint can't combine these dimensions
  // with discount=true server-side).
  const [showFilters, setShowFilters] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all'); // all | live | scheduled | expired
  const [kindFilter, setKindFilter] = useState('all');     // all | percent | fixed
  const [priceMin, setPriceMin] = useState('');
  const [priceMax, setPriceMax] = useState('');
  const PAGE_SIZE = 20;

  // ── Campaigns tab state ───────────────────────────────────────────
  const [activeTab, setActiveTab] = useState('discounts'); // 'discounts' | 'campaigns'
  const [campaigns, setCampaigns] = useState([]);
  const [campaignsLoading, setCampaignsLoading] = useState(false);
  const [showCampaignModal, setShowCampaignModal] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState(null);
  const [expandedCampaignId, setExpandedCampaignId] = useState(null);
  const [campaignProductsByCampaign, setCampaignProductsByCampaign] = useState({});
  const [showBulkAddForCampaignId, setShowBulkAddForCampaignId] = useState(null);

  const loadCampaigns = async () => {
    try {
      setCampaignsLoading(true);
      const r = await fetchCampaigns();
      setCampaigns(r?.data?.campaigns || []);
    } catch (err) {
      // soft-fail; tab will show empty list
    } finally {
      setCampaignsLoading(false);
    }
  };

  const loadCampaignProducts = async (campaign) => {
    try {
      const r = await fetchCampaignProducts(campaign.slug);
      setCampaignProductsByCampaign(prev => ({
        ...prev,
        [campaign._id]: r?.data?.products || [],
      }));
    } catch (err) {
      // soft-fail
    }
  };

  useEffect(() => {
    // Load on first mount so the "Add Discount" dropdown can populate too.
    loadCampaigns();
  }, []);

  useEffect(() => {
    if (!expandedCampaignId) return;
    const c = campaigns.find(x => x._id === expandedCampaignId);
    if (c) loadCampaignProducts(c);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expandedCampaignId]);

  // Load the ENTIRE active-discount set (paging through the endpoint) so the
  // search + filter panel below can operate over everything and paginate
  // client-side. The legacy `page` argument is accepted but ignored — callers
  // throughout the file still pass `currentPage`, which no longer triggers a
  // network fetch (handlePageChange is now purely a slice change).
  const fetchDiscounts = async () => {
    const FETCH_LIMIT = 200; // backend caps at 200/page
    const MAX_PAGES = 50;    // safety guard: 50 * 200 = 10k rows

    const loadAll = async (fetchPage, extract) => {
      const acc = [];
      let p = 1;
      let pages = 1;
      do {
        const r = await fetchPage(p, FETCH_LIMIT);
        if (!r?.success) return null;
        const { items, totalPages } = extract(r);
        acc.push(...items);
        pages = totalPages || 1;
        p += 1;
      } while (p <= pages && p <= MAX_PAGES);
      return acc;
    };

    try {
      setIsLoading(true);
      setError(null);

      // Try legacy endpoint first since we know it works.
      try {
        const all = await loadAll(fetchAllDiscounts, (r) => ({
          items: r.data.products || [],
          totalPages: r.data.pages,
        }));
        if (all) {
          setDiscountProducts(all);
          setTotalDiscounts(all.length);
          setUseUnifiedEndpoint(false);
          return;
        }
      } catch (legacyError) {
        // fall through to unified endpoint
      }

      // Fallback to unified endpoint.
      try {
        const all = await loadAll(fetchDiscountProducts, (r) => ({
          items: r.items || [],
          totalPages: r.pages,
        }));
        if (all) {
          setDiscountProducts(all);
          setTotalDiscounts(all.length);
          setUseUnifiedEndpoint(true);
          return;
        }
      } catch (unifiedError) {
        // both endpoints failed; surface error below
      }

      setError(t('discounts.errors.fetchBothEndpoints'));
    } catch (error) {
      setError(t('discounts.errors.fetchDiscounts'));
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchDiscounts();
  }, []);

  const handleAdd = async () => {
    try {
      const amount = parseFloat(newProduct.amount);
      if (!newProduct.pharmacode || !Number.isFinite(amount) || amount <= 0) {
        setError(t('discounts.errors.fillAllFields'));
        return;
      }
      if (newProduct.kind === 'percent' && amount >= 100) {
        setError('Percentage must be less than 100');
        return;
      }
      if (newProduct.startsAt && newProduct.endsAt &&
          new Date(newProduct.startsAt) >= new Date(newProduct.endsAt)) {
        setError('End date must be after start date');
        return;
      }

      setIsAddingDiscount(true);
      setError(null);

      const rule = {
        kind: newProduct.kind,
        amount,
        startsAt: newProduct.startsAt ? new Date(newProduct.startsAt).toISOString() : undefined,
        endsAt: newProduct.endsAt ? new Date(newProduct.endsAt).toISOString() : undefined,
        label: newProduct.label || undefined,
      };

      const response = await updateProductDiscount(newProduct.pharmacode, rule);

      if (response.success) {
        await fetchDiscounts(currentPage);
        setNewProduct({
          pharmacode: '',
          kind: 'percent',
          amount: '',
          startsAt: '',
          endsAt: '',
          label: '',
        });
        setShowAddForm(false);
      }
    } catch (error) {
      if (
        error?.response?.data?.error === 'PPUB price not found' ||
        error?.response?.data?.error?.toLowerCase?.().includes('ppub price not found')
      ) {
        setError(t('discounts.errors.noPublicPrice'));
      } else {
        setError(error.response?.data?.message || error.response?.data?.error || t('discounts.errors.addDiscount'));
      }
    } finally {
      setIsAddingDiscount(false);
    }
  };

  const handleDelete = async (article) => {
    if (window.confirm(t('discounts.success.confirmRemove'))) {
      try {
        await removeProductDiscount(article.pharmacode);
        await fetchDiscounts(currentPage); // Refresh the list
        setError(null);
      } catch (error) {
        setError(error.response?.data?.message || t('discounts.errors.removeDiscount'));
      }
    }
  };

  const handlePageChange = (page) => {
    // Client-side pagination over the in-memory filtered set — no refetch.
    setCurrentPage(page);
  };

  // Selection functions
  const handleSelectDiscount = (discount) => {
    setSelectedDiscounts(prevSelected => {
      const isAlreadySelected = prevSelected.some(d => d.pharmacode === discount.pharmacode);
      if (isAlreadySelected) {
        return prevSelected.filter(d => d.pharmacode !== discount.pharmacode);
      } else {
        return [...prevSelected, discount];
      }
    });
  };

  const handleExitSelectionMode = () => {
    setIsSelectionMode(false);
    setSelectedDiscounts([]);
  };

  const handleBulkDelete = async () => {
    if (selectedDiscounts.length === 0) {
      setError(t('discounts.errors.selectOne'));
      return;
    }

    if (!deleteReason) {
      setError(t('discounts.errors.provideReason'));
      return;
    }

    try {
      setIsBulkDeleting(true);
      setError(null);

      // Remove each selected discount
      for (const discount of selectedDiscounts) {
        await removeProductDiscount(discount.pharmacode);
      }

      // Refresh the data
      await fetchDiscounts(currentPage);
      
      setSelectedDiscounts([]);
      setIsBulkDeleteModalOpen(false);
      setDeleteReason('');
      setError(null);
      setSuccessMessage(t('discounts.success.bulkRemove', { count: selectedDiscounts.length }));
      
      // Clear success message after 5 seconds
      setTimeout(() => {
        setSuccessMessage('');
      }, 5000);
    } catch (error) {
      setError(error.response?.data?.message || t('discounts.errors.removeDiscounts'));
    } finally {
      setIsBulkDeleting(false);
    }
  };

  // Text search (pharmacode / description) over the full loaded set.
  const searchedProducts = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return discountProducts;
    return discountProducts.filter((product) =>
      (product.articles || []).some((article) =>
        article.description?.longDescription?.toLowerCase().includes(q) ||
        article.description?.description?.toLowerCase().includes(q) ||
        String(article.pharmacode ?? '').includes(searchTerm.trim())
      )
    );
  }, [discountProducts, searchTerm]);

  // Filter panel: status, discount type, and price range.
  const filteredProducts = useMemo(() => {
    const min = priceMin !== '' ? Number(priceMin) : null;
    const max = priceMax !== '' ? Number(priceMax) : null;
    return searchedProducts.filter((product) => {
      if (statusFilter !== 'all' && discountStatusOf(product) !== statusFilter) return false;
      if (kindFilter !== 'all' && discountKindOf(product) !== kindFilter) return false;
      if (min != null || max != null) {
        const price = discountPriceOf(product);
        if (price == null || Number.isNaN(price)) return false;
        if (min != null && price < min) return false;
        if (max != null && price > max) return false;
      }
      return true;
    });
  }, [searchedProducts, statusFilter, kindFilter, priceMin, priceMax]);

  const pageCount = Math.max(Math.ceil(filteredProducts.length / PAGE_SIZE), 1);
  const pageProducts = filteredProducts.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE
  );
  const activeFilterCount =
    (statusFilter !== 'all' ? 1 : 0) +
    (kindFilter !== 'all' ? 1 : 0) +
    (priceMin !== '' || priceMax !== '' ? 1 : 0);

  const clearAllFilters = () => {
    setStatusFilter('all');
    setKindFilter('all');
    setPriceMin('');
    setPriceMax('');
  };

  // Reset to page 1 whenever the result set changes shape.
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, statusFilter, kindFilter, priceMin, priceMax]);

  // Clamp the page if the filtered set shrank below the current page.
  useEffect(() => {
    if (currentPage > pageCount) setCurrentPage(pageCount);
  }, [pageCount, currentPage]);


  return (
    <div className="bg-white">
      <div className="mx-auto max-w-[95%] px-2 sm:px-4 lg:px-6">
        {/* === Tab toggle: Single Discounts | Campaigns === */}
        <div className="pt-6 pb-2">
          <div
            role="tablist"
            aria-label={t('discounts.tabs.label')}
            className="inline-flex bg-white border border-gray-200 rounded-lg p-1 shadow-sm"
          >
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === 'discounts'}
              onClick={() => setActiveTab('discounts')}
              className={`inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                activeTab === 'discounts'
                  ? 'bg-indigo-600 text-white shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <FaPercent className="text-xs" />
              <span>{t('discounts.tabs.singleDiscounts')}</span>
              <span
                className={`ml-1 px-1.5 min-w-[1.25rem] text-center rounded-full text-[11px] font-semibold tabular-nums ${
                  activeTab === 'discounts'
                    ? 'bg-white/20 text-white'
                    : 'bg-gray-100 text-gray-600'
                }`}
              >
                {totalDiscounts.toLocaleString()}
              </span>
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === 'campaigns'}
              onClick={() => setActiveTab('campaigns')}
              className={`inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                activeTab === 'campaigns'
                  ? 'bg-indigo-600 text-white shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <FaBolt className="text-xs" />
              <span>{t('discounts.tabs.campaigns')}</span>
              <span
                className={`ml-1 px-1.5 min-w-[1.25rem] text-center rounded-full text-[11px] font-semibold tabular-nums ${
                  activeTab === 'campaigns'
                    ? 'bg-white/20 text-white'
                    : 'bg-gray-100 text-gray-600'
                }`}
              >
                {campaigns.length}
              </span>
            </button>
          </div>
        </div>

        {/* Campaigns tab content — rendered above the existing Single Discounts UI when active */}
        {activeTab === 'campaigns' && (
          <div className="py-6">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-lg font-semibold text-gray-900">Campaigns</h2>
              <button
                type="button"
                onClick={() => setShowCampaignModal(true)}
                className="px-3 py-1.5 rounded-lg bg-[#E1444B] text-white text-sm font-medium hover:opacity-90"
              >+ New Campaign</button>
            </div>

            {campaignsLoading ? (
              <div className="py-8 text-center text-gray-500">Loading...</div>
            ) : campaigns.length === 0 ? (
              <div className="py-8 text-center text-gray-500">No campaigns yet.</div>
            ) : (
              <ul className="divide-y divide-gray-200 rounded-lg border border-gray-200 bg-white">
                {campaigns.map((c) => (
                  <li key={c._id} className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      {c.accentColor && (
                        <span
                          className="w-3 h-3 rounded-full flex-shrink-0"
                          style={{ background: c.accentColor }}
                        />
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-gray-900 truncate">{c.name}</div>
                        <div className="text-xs text-gray-500 truncate">
                          {c.productCount} product{c.productCount === 1 ? '' : 's'} ·{' '}
                          {c.startsAt ? new Date(c.startsAt).toLocaleDateString() : '∞'}
                          {' → '}
                          {c.endsAt ? new Date(c.endsAt).toLocaleDateString() : '∞'}
                        </div>
                      </div>
                      <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                        c.status === 'live' ? 'bg-green-100 text-green-700' :
                        c.status === 'scheduled' ? 'bg-blue-100 text-blue-700' :
                        c.status === 'paused' ? 'bg-amber-100 text-amber-700' :
                        'bg-gray-100 text-gray-600'
                      }`}>{c.status}</span>
                      <button
                        type="button"
                        onClick={() => setExpandedCampaignId(c._id === expandedCampaignId ? null : c._id)}
                        className="px-3 py-1 rounded-lg border border-gray-300 text-sm hover:bg-gray-50"
                      >{c._id === expandedCampaignId ? 'Close' : 'Open'}</button>
                    </div>

                    {expandedCampaignId === c._id && (
                      <div className="mt-3 pt-3 border-t border-gray-100">
                        <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                          <div className="text-sm font-medium text-gray-700">Products in this campaign</div>
                          <div className="flex gap-2 flex-wrap">
                            <button
                              type="button"
                              onClick={() => setShowBulkAddForCampaignId(c._id)}
                              className="px-3 py-1 rounded-lg bg-[#E1444B] text-white text-xs font-medium hover:opacity-90"
                            >+ Add products</button>
                            <button
                              type="button"
                              onClick={() => setEditingCampaign(c)}
                              className="px-3 py-1 rounded-lg border border-gray-300 text-xs hover:bg-gray-50"
                            >Edit</button>
                            <button
                              type="button"
                              onClick={async () => {
                                await updateCampaign(c._id, { isActive: !c.isActive });
                                loadCampaigns();
                              }}
                              className="px-3 py-1 rounded-lg border border-gray-300 text-xs hover:bg-gray-50"
                            >{c.isActive ? 'Pause' : 'Resume'}</button>
                          </div>
                        </div>

                        {(campaignProductsByCampaign[c._id] || []).length === 0 ? (
                          <div className="text-xs text-gray-500 py-4 text-center">No products yet.</div>
                        ) : (
                          <ul className="text-sm space-y-1">
                            {(campaignProductsByCampaign[c._id] || []).map((p) => {
                              const article = p.articles?.[0];
                              const code = article?.pharmacode;
                              const title = (Array.isArray(article?.consumerFolder) ? article.consumerFolder[0]?.title : article?.consumerFolder?.title)
                                || article?.description?.longDescription || article?.longDescription || `pharmacode ${code}`;
                              return (
                                <li key={p._id} className="flex items-center gap-3 px-2 py-1.5 border border-gray-100 rounded">
                                  <span className="font-mono text-xs text-gray-500">{code}</span>
                                  <span className="flex-1 truncate">{title}</span>
                                  <span className="text-xs text-gray-500">
                                    {article?.price?.currentPrice != null ? formatMoneyCHF(article.price.currentPrice) : '—'}
                                  </span>
                                  <button
                                    type="button"
                                    onClick={async () => {
                                      if (!window.confirm(`Remove pharmacode ${code} from this campaign?`)) return;
                                      await removeProductFromCampaign(c._id, code);
                                      loadCampaignProducts(c);
                                      loadCampaigns();
                                    }}
                                    className="px-2 py-0.5 rounded text-xs text-red-600 hover:bg-red-50"
                                  >Remove</button>
                                </li>
                              );
                            })}
                          </ul>
                        )}
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {/* === Existing Single Discounts UI — hidden when on Campaigns tab === */}
        <div style={{ display: activeTab === 'discounts' ? undefined : 'none' }}>
        {/* Modern Header Section */}
        <div className="pb-8 pt-10">
          {/* Main Header */}
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-6">
            <div className="flex items-center gap-4 mb-4 lg:mb-0">
              <div className="flex items-center justify-center w-12 h-12 bg-gradient-to-r from-red-500 to-pink-600 rounded-xl shadow-lg">
                <FaPercent className="text-white text-xl" />
              </div>
              <div>
                <h1 className="text-3xl font-bold tracking-tight text-gray-900">
                  {t('discounts.title')}
                  {isSelectionMode && (
                    <span className="ml-3 inline-flex items-center gap-2 px-3 py-1 bg-gradient-to-r from-indigo-50 to-purple-50 border border-indigo-200 rounded-lg text-lg font-medium text-indigo-700">
                      <div className="w-2 h-2 bg-indigo-500 rounded-full animate-pulse"></div>
                      {t('discounts.selectionMode')}
                    </span>
                  )}
                </h1>
                <p className="text-sm text-gray-500 mt-1">
                  {isSelectionMode 
                    ? t('discounts.subtitleSelectionMode')
                    : t('discounts.subtitle')
                  }
                  {pageCount > 1 && (
                    <span className="ml-2 inline-flex items-center gap-1 px-2 py-0.5 bg-blue-100 text-blue-700 text-xs font-medium rounded-full">
                      <div className="w-1.5 h-1.5 bg-blue-500 rounded-full"></div>
                      {pageCount} {t('discounts.pages')}
                    </span>
                  )}
                  {useUnifiedEndpoint && (
                    <span className="ml-2 inline-flex items-center gap-1 px-2 py-0.5 bg-green-100 text-green-700 text-xs font-medium rounded-full">
                      <div className="w-1.5 h-1.5 bg-green-500 rounded-full"></div>
                      {t('discounts.unifiedApi')}
                    </span>
                  )}
                </p>
              </div>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={() => setShowAddForm(true)}
                className="inline-flex items-center gap-2 bg-white text-red-600 border border-red-300 px-4 py-2.5 rounded-lg hover:bg-red-50 hover:border-red-500 transition-all duration-200 shadow-sm font-medium"
              >
                <FaEdit className="text-sm" />
                {t('discounts.addNewDiscount')}
              </button>
              <button
                onClick={() => setShowBulkModal(true)}
                className="inline-flex items-center gap-2 bg-gradient-to-r from-red-500 to-pink-600 text-white px-4 py-2.5 rounded-lg hover:from-red-600 hover:to-pink-700 transition-all duration-200 shadow-md hover:shadow-lg font-medium"
              >
                <FaBolt className="text-sm" />
                Bulk discount
              </button>
              <button
                onClick={() => setShowImportModal(true)}
                className="inline-flex items-center gap-2 bg-white text-red-600 border border-red-300 px-4 py-2.5 rounded-lg hover:bg-red-50 hover:border-red-500 transition-all duration-200 shadow-sm font-medium"
              >
                <FaImage className="text-sm" />
                Import from file
              </button>
              <button
                onClick={() => fetchDiscounts(currentPage)}
                className="inline-flex items-center gap-2 bg-gradient-to-r from-green-500 to-emerald-600 text-white px-4 py-2.5 rounded-lg hover:from-green-600 hover:to-emerald-700 transition-all duration-200 shadow-md hover:shadow-lg"
              >
                <FaSync className="text-sm" />
                {t('discounts.refresh')}
              </button>
            </div>
          </div>

          {/* Statistics Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div className="bg-gradient-to-r from-red-50 to-pink-50 border border-red-200 rounded-xl p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-red-600">{t('discounts.stats.totalDiscounts')}</p>
                  <p className="text-2xl font-bold text-red-900">
                    {isLoading ? (
                      <span className="inline-block animate-pulse bg-red-200 h-8 w-16 rounded"></span>
                    ) : (
                      totalDiscounts.toLocaleString()
                    )}
                  </p>
                </div>
                <div className="flex items-center justify-center w-10 h-10 bg-red-100 rounded-lg">
                  <FaPercent className="text-red-600" />
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-xl p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-green-600">{t('discounts.stats.showing')}</p>
                  <p className="text-2xl font-bold text-green-900">
                    {isLoading ? (
                      <span className="inline-block animate-pulse bg-green-200 h-8 w-16 rounded"></span>
                    ) : (
                      `${filteredProducts.length} ${t('discounts.stats.products')}`
                    )}
                  </p>
                </div>
                <div className="flex items-center justify-center w-10 h-10 bg-green-100 rounded-lg">
                  <FaFilter className="text-green-600" />
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-r from-purple-50 to-pink-50 border border-purple-200 rounded-xl p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-purple-600">{t('discounts.stats.currentPage')}</p>
                  <p className="text-2xl font-bold text-purple-900">
                    {isLoading ? (
                      <span className="inline-block animate-pulse bg-purple-200 h-8 w-16 rounded"></span>
                    ) : (
                      `${currentPage} of ${pageCount}`
                    )}
                  </p>
                </div>
                <div className="flex items-center justify-center w-10 h-10 bg-purple-100 rounded-lg">
                  <FaSearch className="text-purple-600" />
                </div>
              </div>
            </div>
          </div>

          {/* Search Bar + Filters toggle */}
          <div className="flex flex-col lg:flex-row gap-3 lg:items-center">
            <div className="relative flex-1">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <FaSearch className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                placeholder={t('discounts.search.placeholder')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="block w-full pl-10 pr-3 py-2.5 border border-gray-300 rounded-lg leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all duration-200"
              />
            </div>
            <button
              onClick={() => setShowFilters((open) => !open)}
              className={`inline-flex items-center justify-center gap-2.5 px-6 py-2.5 rounded-lg font-medium transition-all duration-200 shadow-sm flex-shrink-0 ${
                showFilters || activeFilterCount > 0
                  ? 'bg-gradient-to-r from-red-500 to-pink-600 text-white shadow-md'
                  : 'bg-white text-gray-700 border-2 border-gray-200 hover:border-red-300 hover:bg-red-50'
              }`}
            >
              <FaFilter className={showFilters || activeFilterCount > 0 ? 'text-white' : 'text-red-500'} />
              <span>Filters</span>
              {activeFilterCount > 0 && (
                <span className="inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 rounded-full bg-white/25 text-xs font-semibold">
                  {activeFilterCount}
                </span>
              )}
            </button>
          </div>

          {/* Filters Panel */}
          {showFilters && (
            <div className="mt-4 bg-white border border-gray-200 rounded-xl p-4 shadow-sm">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Status filter */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Status</label>
                  <div className="flex flex-wrap gap-2">
                    {[
                      { value: 'all', label: 'All' },
                      { value: 'live', label: 'Live' },
                      { value: 'scheduled', label: 'Scheduled' },
                      { value: 'expired', label: 'Expired' },
                    ].map((opt) => (
                      <button
                        key={opt.value}
                        onClick={() => setStatusFilter(opt.value)}
                        className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                          statusFilter === opt.value
                            ? 'bg-red-500 text-white'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Type filter */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Type</label>
                  <div className="flex flex-wrap gap-2">
                    {[
                      { value: 'all', label: 'All' },
                      { value: 'percent', label: 'Percentage' },
                      { value: 'fixed', label: 'Fixed price' },
                    ].map((opt) => (
                      <button
                        key={opt.value}
                        onClick={() => setKindFilter(opt.value)}
                        className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                          kindFilter === opt.value
                            ? 'bg-red-500 text-white'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Price range filter */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Price (CHF)</label>
                  <div className="flex items-center gap-2">
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      placeholder="Min"
                      value={priceMin}
                      onChange={(e) => setPriceMin(e.target.value)}
                      className="w-full px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-red-500 focus:border-red-500"
                    />
                    <span className="text-gray-400">–</span>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      placeholder="Max"
                      value={priceMax}
                      onChange={(e) => setPriceMax(e.target.value)}
                      className="w-full px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-red-500 focus:border-red-500"
                    />
                  </div>
                </div>
              </div>

              {activeFilterCount > 0 && (
                <div className="mt-4 pt-3 border-t border-gray-100 flex items-center justify-between">
                  <span className="text-sm text-gray-500">
                    {filteredProducts.length} of {discountProducts.length} discounts match
                  </span>
                  <button
                    onClick={clearAllFilters}
                    className="inline-flex items-center gap-2 text-sm font-medium text-gray-600 hover:text-gray-900"
                  >
                    <FaSync className="text-xs" /> Clear filters
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Selection Mode Controls */}
          {isSelectionMode && (
            <div className="mt-4 bg-white border border-gray-200 rounded-lg shadow-sm">
              <div className="px-4 py-3 flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={selectedDiscounts.length === pageProducts.length && pageProducts.length > 0}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedDiscounts(pageProducts.map(product => {
                            const a = product.articles?.[0];
                            return {
                              pharmacode: a?.pharmacode ?? product.pharmacode,
                              description: {
                                description: a?.description?.description || 'No description available',
                                longDescription: a?.description?.longDescription || 'No description available'
                              },
                              hasPhoto: a?.hasPhoto || false,
                              images: a?.images || {},
                              price: a?.price || null
                            };
                          }));
                        } else {
                          setSelectedDiscounts([]);
                        }
                      }}
                      className="w-4 h-4 text-indigo-600 bg-gray-100 border-gray-300 rounded focus:ring-indigo-500"
                    />
                    <span className="text-sm font-medium text-gray-700">
                      {t('discounts.selection.selectAll')} ({pageProducts.length})
                    </span>
                  </div>
                  {selectedDiscounts.length > 0 && (
                    <div className="flex items-center gap-2 px-2 py-1 bg-indigo-50 border border-indigo-200 rounded-md">
                      <div className="w-1.5 h-1.5 bg-indigo-500 rounded-full"></div>
                      <span className="text-sm font-medium text-indigo-700">
                        {selectedDiscounts.length} {t('discounts.selection.selected')}
                      </span>
                    </div>
                  )}
                </div>
                
                <div className="flex items-center gap-3">
                  {selectedDiscounts.length > 0 && (
                    <button
                      onClick={() => {
                        setDeleteReason('');
                        setIsBulkDeleteModalOpen(true);
                      }}
                      disabled={isBulkDeleting}
                      className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium transition-all duration-200 ${
                        isBulkDeleting
                          ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                          : 'bg-red-50 text-red-600 hover:bg-red-100 border border-red-200'
                      }`}
                    >
                      {isBulkDeleting ? (
                        <>
                          <ButtonSpinner size="sm" />
                          {t('discounts.selection.removing')}
                        </>
                      ) : (
                        <>
                          <FaTrash className="text-xs" />
                          {t('discounts.selection.remove')} ({selectedDiscounts.length})
                        </>
                      )}
                    </button>
                  )}
                  <button
                    onClick={handleExitSelectionMode}
                    className="inline-flex items-center gap-2 text-gray-500 hover:text-gray-700 text-sm font-medium transition-colors duration-200"
                  >
                    {t('discounts.selection.cancel')}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {error && !showAddForm && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4 flex items-center gap-2">
            <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
            {error}
          </div>
        )}

        {successMessage && (
          <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg mb-4 flex items-center gap-2">
            <FaCheckSquare className="h-5 w-5 text-green-600" />
            {successMessage}
          </div>
        )}

        <section aria-labelledby="discounts-heading" className="pb-24 pt-6">
          {/* Select Discounts Button */}
          {filteredProducts.length > 0 && !isSelectionMode && (
            <div className="mb-6 flex items-center justify-end">
              <button
                onClick={() => setIsSelectionMode(true)}
                className="inline-flex items-center gap-2.5 bg-gradient-to-r from-purple-500 to-indigo-600 text-white px-5 py-2.5 rounded-xl hover:from-purple-600 hover:to-indigo-700 transition-all duration-300 shadow-lg hover:shadow-xl font-medium text-sm group"
              >
                <div className="flex items-center justify-center w-4 h-4 bg-white/20 rounded-md group-hover:bg-white/30 transition-colors duration-200">
                  <FaCheckSquare className="text-xs" />
                </div>
                <span>{t('discounts.selection.selectDiscounts')}</span>
                <div className="w-1.5 h-1.5 bg-white/60 rounded-full group-hover:bg-white/80 transition-colors duration-200"></div>
              </button>
            </div>
          )}

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
            {isLoading ? (
              <CardSkeleton count={8} />
            ) : pageProducts.length > 0 ? (
              pageProducts.map((product) => {
                const a = product.articles?.[0];
                const processedArticle = {
                  pharmacode: a?.pharmacode ?? product.pharmacode,
                  description: {
                    description: a?.description?.description || 'No description available',
                    longDescription: a?.description?.longDescription || 'No description available'
                  },
                  hasPhoto: a?.hasPhoto || false,
                  images: a?.images || {},
                  price: a?.price || null
                };

                const isSelected = selectedDiscounts.some(selected => selected.pharmacode === processedArticle.pharmacode);

                // Key on discount _id (unique per row) with the article
                // pharmacode as a fallback. Avoids collisions when two
                // discounted SKUs share the same parent product.
                const rowKey = product.articles?.[0]?.discount?._id
                  || `${product._id}-${processedArticle.pharmacode}`;
                return (
                  <DiscountProductCard
                    key={rowKey}
                    product={product}
                    onDelete={handleDelete}
                    onSelect={handleSelectDiscount}
                    isSelected={isSelected}
                    isSelectionMode={isSelectionMode}
                  />
                );
              })
            ) : (
              <div className="text-center col-span-full">
                <h3 className="text-lg font-medium text-gray-900">{t('discounts.empty.title')}</h3>
                <p className="text-gray-500">
                  {searchTerm 
                    ? t('discounts.empty.withSearch')
                    : t('discounts.empty.noSearch')
                  }
                </p>
              </div>
            )}
          </div>

          {/* Pagination */}
          {pageCount > 1 && (
            <Pagination
              currentPage={currentPage}
              totalPages={pageCount}
              onPageChange={handlePageChange}
              totalProducts={filteredProducts.length}
              isLoading={isLoading}
              showProductInfo={true}
            />
          )}
        </section>
        </div>{/* close: existing-discounts-UI wrapper */}
      </div>

      {/* Campaigns modals */}
      {showCampaignModal && (
        <CampaignFormModal
          campaign={null}
          onClose={() => setShowCampaignModal(false)}
          onSaved={() => { setShowCampaignModal(false); loadCampaigns(); }}
        />
      )}
      {editingCampaign && (
        <CampaignFormModal
          campaign={editingCampaign}
          onClose={() => setEditingCampaign(null)}
          onSaved={() => { setEditingCampaign(null); loadCampaigns(); }}
        />
      )}
      {showBulkAddForCampaignId && (
        <CampaignBulkAddModal
          campaign={campaigns.find(c => c._id === showBulkAddForCampaignId)}
          onClose={() => setShowBulkAddForCampaignId(null)}
          onCommitted={() => {
            const c = campaigns.find(x => x._id === showBulkAddForCampaignId);
            setShowBulkAddForCampaignId(null);
            if (c) loadCampaignProducts(c);
            loadCampaigns();
          }}
        />
      )}

      {/* Add New Discount Modal */}
      {showAddForm && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm transition-opacity" 
            onClick={() => setShowAddForm(false)} 
          />
          
          <div className="flex min-h-screen items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
              className="relative bg-white/95 backdrop-blur-sm rounded-2xl max-w-md w-full z-50 shadow-2xl border border-gray-200/50"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="flex items-center justify-between p-6 border-b border-gray-200/50">
                <div className="flex items-center gap-3">
                  <div className="flex items-center justify-center w-10 h-10 bg-gradient-to-r from-red-500 to-pink-600 rounded-xl shadow-lg">
                    <FaEdit className="text-white text-lg" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-gray-900">{t('discounts.modals.add.title')}</h2>
                    <p className="text-sm text-gray-500">{t('discounts.modals.add.subtitle')}</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowAddForm(false)}
                  className="text-gray-400 hover:text-gray-600 p-2 hover:bg-gray-100 rounded-lg transition-all duration-200"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              
              {/* Content */}
              <div className="p-6">
                <div className="space-y-6">
                  {error && showAddForm && (
                    <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4 flex items-center gap-2">
                      <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                      </svg>
                      {error}
                    </div>
                  )}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      {t('discounts.modals.add.pharmacodeLabel')} <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <ProductAutocompleteInput
                        value={newProduct.pharmacode}
                        onChange={val => setNewProduct({ ...newProduct, pharmacode: val })}
                        onSelectProduct={product => setNewProduct({ ...newProduct, pharmacode: product.articles?.[0]?.pharmacode || product.pharmacode || product.productNumber })}
                        onShowMoreResults={term => { setProductSearchTerm(term); setShowProductSearchModal(true); }}
                        placeholder={t('products.search.placeholder')}
                      />
                    </div>
                    <p className="mt-2 text-xs text-gray-500">
                      {t('discounts.modals.add.pharmacodeHint')}
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Discount type <span className="text-red-500">*</span>
                    </label>
                    <div className="grid grid-cols-2 gap-3">
                      <button
                        type="button"
                        onClick={() => setNewProduct({ ...newProduct, kind: 'percent' })}
                        className={`px-4 py-3 rounded-xl border font-medium transition-all duration-200 ${
                          newProduct.kind === 'percent'
                            ? 'bg-red-500 text-white border-red-500 shadow-md'
                            : 'bg-white text-gray-700 border-gray-300 hover:border-gray-400'
                        }`}
                      >
                        Percentage (%)
                      </button>
                      <button
                        type="button"
                        onClick={() => setNewProduct({ ...newProduct, kind: 'fixed' })}
                        className={`px-4 py-3 rounded-xl border font-medium transition-all duration-200 ${
                          newProduct.kind === 'fixed'
                            ? 'bg-red-500 text-white border-red-500 shadow-md'
                            : 'bg-white text-gray-700 border-gray-300 hover:border-gray-400'
                        }`}
                      >
                        Fixed price (CHF)
                      </button>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      {newProduct.kind === 'percent'
                        ? 'Percentage off'
                        : 'Final price (CHF)'} <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <input
                        type="number"
                        step={newProduct.kind === 'percent' ? '1' : '0.01'}
                        min="0"
                        max={newProduct.kind === 'percent' ? '99' : undefined}
                        value={newProduct.amount}
                        onChange={(e) => setNewProduct({ ...newProduct, amount: e.target.value })}
                        placeholder={newProduct.kind === 'percent' ? 'e.g. 20' : 'e.g. 8.50'}
                        className="w-full px-4 py-3 pr-16 border border-gray-300 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all duration-200 bg-white/80 backdrop-blur-sm"
                        required
                      />
                      <span className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 font-medium">
                        {newProduct.kind === 'percent' ? '%' : 'CHF'}
                      </span>
                    </div>
                    <p className="mt-2 text-xs text-gray-500">
                      {newProduct.kind === 'percent'
                        ? 'Discount as a percentage of the current public price (1–99). Stays correct if the base price changes.'
                        : 'Fixed final price the customer pays. Must be lower than the public price.'}
                    </p>
                  </div>

                  {/* Optional time window */}
                  <details className="group">
                    <summary className="cursor-pointer text-sm font-semibold text-gray-700 hover:text-gray-900 select-none">
                      Schedule (optional)
                    </summary>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-3 pl-2">
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Starts at</label>
                        <input
                          type="datetime-local"
                          value={newProduct.startsAt}
                          onChange={(e) => setNewProduct({ ...newProduct, startsAt: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all duration-200 bg-white/80"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Ends at</label>
                        <input
                          type="datetime-local"
                          value={newProduct.endsAt}
                          onChange={(e) => setNewProduct({ ...newProduct, endsAt: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all duration-200 bg-white/80"
                        />
                      </div>
                      <div className="sm:col-span-2">
                        <label className="block text-xs font-medium text-gray-600 mb-1">Label</label>
                        <input
                          type="text"
                          value={newProduct.label}
                          onChange={(e) => setNewProduct({ ...newProduct, label: e.target.value })}
                          placeholder="e.g. Spring Sale 2026"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all duration-200 bg-white/80"
                        />
                      </div>
                    </div>
                    <p className="mt-2 pl-2 text-xs text-gray-500">
                      Leave dates empty for an immediately-active, non-expiring discount.
                    </p>
                  </details>
                </div>
              </div>

              {/* Footer */}
              <div className="flex justify-end gap-3 p-6 border-t border-gray-200/50 bg-gray-50/50 rounded-b-2xl">
                <button
                  onClick={() => {
                    setShowAddForm(false);
                    setNewProduct({
                      pharmacode: '',
                      kind: 'percent',
                      amount: '',
                      startsAt: '',
                      endsAt: '',
                      label: '',
                    });
                  }}
                  disabled={isAddingDiscount}
                  className="px-6 py-2.5 text-gray-700 bg-white border border-gray-300 rounded-xl hover:bg-gray-50 hover:border-gray-400 transition-all duration-200 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {t('discounts.modals.add.cancel')}
                </button>
                <button
                  onClick={handleAdd}
                  disabled={isAddingDiscount}
                  className="px-6 py-2.5 bg-gradient-to-r from-red-500 to-pink-600 text-white rounded-xl hover:from-red-600 hover:to-pink-700 transition-all duration-200 font-medium shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                >
                  <span className="flex items-center gap-2">
                    {isAddingDiscount ? (
                      <ButtonSpinner size="sm" />
                    ) : (
                      <FaEdit className="text-sm" />
                    )}
                    {isAddingDiscount ? t('discounts.modals.add.adding') : t('discounts.modals.add.add')}
                  </span>
                </button>
              </div>
            </motion.div>
          </div>
        </div>
      )}

      {/* Product Search Modal for more results */}
      <ProductSearchModal
        open={showProductSearchModal}
        onClose={() => setShowProductSearchModal(false)}
        searchTerm={productSearchTerm}
        onSelectProduct={product => {
          setNewProduct({ ...newProduct, pharmacode: product.articles?.[0]?.pharmacode || product.pharmacode || product.productNumber });
          setShowProductSearchModal(false);
        }}
      />

      {/* Bulk Discount Modal */}
      <BulkDiscountModal
        isOpen={showBulkModal}
        onClose={() => setShowBulkModal(false)}
        onSuccess={() => {
          // Refresh the list after a successful bulk apply so the new
          // discounts show up immediately. Don't auto-close so the admin
          // can see the summary; they close manually.
          fetchDiscounts(currentPage);
        }}
      />

      {/* File-import Discount Modal (PDF / future: CSV / XLSX) */}
      <DiscountImportModal
        isOpen={showImportModal}
        onClose={() => setShowImportModal(false)}
        onSuccess={() => fetchDiscounts(currentPage)}
      />

      {/* Bulk Delete Modal */}
      {isBulkDeleteModalOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="fixed inset-0 bg-black bg-opacity-50 transition-opacity" 
               onClick={() => setIsBulkDeleteModalOpen(false)}
          />
          <div className="flex min-h-screen items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="relative bg-white rounded-lg max-w-lg w-full z-50"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-6">
                <h3 className="text-lg font-medium mb-4">{t('discounts.modals.bulkDelete.title')}</h3>
                <p className="text-sm text-gray-700 mb-4">
                  {t('discounts.modals.bulkDelete.description', { count: selectedDiscounts.length })}
                </p>
                <textarea
                  placeholder={t('discounts.modals.bulkDelete.placeholder')}
                  value={deleteReason}
                  onChange={(e) => setDeleteReason(e.target.value)}
                  className="w-full border rounded-md p-2 mb-4"
                  rows="3"
                />
                <div className="flex justify-end gap-4">
                  <button
                    onClick={() => setIsBulkDeleteModalOpen(false)}
                    className="px-4 py-2 border rounded-md"
                  >
                    {t('discounts.modals.bulkDelete.cancel')}
                  </button>
                  <button
                    onClick={handleBulkDelete}
                    disabled={isBulkDeleting}
                    className={`px-4 py-2 rounded-md transition-all duration-200 ${
                      isBulkDeleting 
                        ? 'bg-gray-400 cursor-not-allowed' 
                        : 'bg-red-500 hover:bg-red-600'
                    } text-white flex items-center gap-2`}
                  >
                    {isBulkDeleting ? (
                      <>
                        <ButtonSpinner size="sm" />
                        {t('discounts.modals.bulkDelete.removing')}
                      </>
                    ) : (
                      t('discounts.modals.bulkDelete.remove')
                    )}
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DiscountManager;